import Spinner from '@atlaskit/spinner';
import React, { createContext, useContext, useReducer, ReactNode, useEffect, useRef } from 'react';

import { handleLogin } from '../components/common/handle-login-utils';
import { UserProfile, SubscriptionDetails } from '../components/common/types';
import { userProfile, subscriptionDetails } from '../mock-data'

// Types
export interface AppState {
    userProfile: UserProfile | null;
    subscriptionDetails: SubscriptionDetails;
    loading: boolean;
    error: string | null;
}

// Action types
export type AppAction =
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_USER_PROFILE'; payload: UserProfile }
    | { type: 'SET_SUBSCRIPTION_DETAILS'; payload: SubscriptionDetails }
    | { type: 'SET_ERROR'; payload: string }
    | { type: 'RESET_STATE' };

// Initial state
export const initialState: AppState = {
    userProfile: null,
    subscriptionDetails: [],
    loading: false,
    error: null,
};

// Reducer
export const appReducer = (state: AppState, action: AppAction): AppState => {
    switch (action.type) {
        case 'SET_LOADING':
            return {
                ...state,
                loading: action.payload,
            };
        case 'SET_USER_PROFILE':
            return {
                ...state,
                userProfile: action.payload,
                error: null,
            };
        case 'SET_SUBSCRIPTION_DETAILS':
            return {
                ...state,
                subscriptionDetails: action.payload,
                error: null,
            };
        case 'SET_ERROR':
            return {
                ...state,
                error: action.payload,
                loading: false,
            };
        case 'RESET_STATE':
            return initialState;
        default:
            return state;
    }
};

// Context
interface AppContextType {
    state: AppState;
    dispatch: React.Dispatch<AppAction>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// API functions
const fetchUserProfile = async (): Promise<UserProfile> => {
    const response = await fetch('/gateway/api/me', {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        throw response;
    }

    const data = await response.json();
    return data;
};

const fetchSubscriptionDetails = async (aaid: string, email: string): Promise<SubscriptionDetails> => {
    try {
        const url = `/gateway/api/rovo-slack-svc/api/v1/cloudSites/list-admin-sites`
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                aaid,
                email,
            }),
        });

        if (!response.ok) {
            throw response;
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error("Error while fetching subscription details");
        return []
    }
};

// Provider component
interface AppProviderProps {
    children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(appReducer, initialState);
    const isMounted = useRef(true);

    useEffect(() => {
        const fetchAppState = async () => {
            if(window.location.pathname.startsWith('/public/')) {
                return;
            }

            try {
                dispatch(setLoading(true));
                
                // Fetch user profile
                const userProfile = await fetchUserProfile();
                dispatch(setUserProfile(userProfile));
                
                // Fetch subscription details
                const subscriptionDetailsObj = await fetchSubscriptionDetails(userProfile?.account_id, userProfile?.email);
                dispatch(setSubscriptionDetails(subscriptionDetailsObj));
                
            } catch (error: any) {
                if(process.env['NODE_ENV'] === 'local') {
                    dispatch(setUserProfile(userProfile));
                    dispatch(setSubscriptionDetails(subscriptionDetails));
                    return;
                }
                if (error?.status === 401 || error?.statusText?.includes('Unauthorized')) {
                    handleLogin();
                    return;
                }

                dispatch(setError('Failed to fetch user data'));
            } finally {
                if (isMounted.current) {
                    dispatch(setLoading(false));
                }
            }
        };

        fetchAppState();

        return () => {
            isMounted.current = false;
        };
    }, []);

    // Show loading spinner while fetching data
    if (state.loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh'
            }}>
                <Spinner size="large" />
            </div>
        );
    }

    // Don't render children if there's an error and no user profile
    if (state.error && !state.userProfile) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                color: 'red'
            }}>
                {state.error}
            </div>
        );
    }

    return (
        <AppContext.Provider value={{ state, dispatch }}>
            {children}
        </AppContext.Provider>
    );
};

// Custom hook to use the app context
export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};

// Action creators
export const setLoading = (loading: boolean): AppAction => ({
    type: 'SET_LOADING',
    payload: loading,
});

export const setUserProfile = (profile: UserProfile): AppAction => ({
    type: 'SET_USER_PROFILE',
    payload: profile,
});

export const setSubscriptionDetails = (details: SubscriptionDetails): AppAction => ({
    type: 'SET_SUBSCRIPTION_DETAILS',
    payload: details,
});

export const setError = (error: string): AppAction => ({
    type: 'SET_ERROR',
    payload: error,
});

export const resetState = (): AppAction => ({
    type: 'RESET_STATE',
});
